import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, merge, startWith, switchMap, take } from 'rxjs';
import { QuoteService } from 'src/app/private/services/quote.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { DealService } from 'src/app/private/services/deals.service';
import { CompanyService } from 'src/app/private/services/company.service';
import { applyCenterScopeToWhere } from 'src/app/utils/tenant-scope.util';

@Component({
  selector: 'app-quote',
  templateUrl: './quote.component.html',
  styleUrls: ['./quote.component.scss']
})
export class QuoteComponent {

  searchQuery = new FormControl();
  @ViewChild(MatPaginator)
  paginator!: MatPaginator;

  @ViewChild(MatSort)
  sort!: MatSort;

  dateQuery = new FormControl();
  center_id: any;
  totalRows = 0;
  temp: any = [];
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  formAction = 'Add';
  currentPage = 0;
  pageSize = 25;
  pageSizeOptions: number[] = [10, 25, 100];
  btnLoading: boolean = false;
  reloading: boolean = false;
  initialLoad: boolean = true;
  closeResult = '';
  editData: any;
  deleteData: any;
  users: any = [];
  currencyCode: any;
  current_id_hover = '';
  infoArray: any = [];
  isToggled: boolean = false;
  isSaving: boolean = false;
  sendQuoteFromEmail = '';
  sendQuoteToEmail = '';
  sendQuoteEmailProvider = '';

  deals: any = [];

  isAdmin: boolean = false;

  displayedColumns: string[] = [
    "quote_number",
    "account_name", 
    "subject",
    "total_amount",
    "status",
    "quote_date",
    "valid_until",
    "action"
  ];

  defaultFilter = [
    "quote_number",
    "account_name",
    "total_amount",
    "status",
    "quote_date",
    "valid_until"
  ];

  importColumnsArray: any = [
    { key: 'quote_number', value: 'Quote Number' },
    { key: 'account_name', value: 'Account Name' },
    { key: 'total_amount', value: 'Total Amount' },
    { key: 'status', value: 'Status' },
    { key: 'quote_date', value: 'Quote Date' },
    { key: 'valid_until', value: 'Valid Until' },
  ];

  constructor(
    private localStorage: LocalStorageService,
    private api: QuoteService,
    private modalService: NgbModal,
    private toast: ToastrService,
    private userApi: UsersService,
    private tracker: TrackingService,
    private dealService: DealService,
    private companyService: CompanyService,
    private route: ActivatedRoute,
    private router: Router
  ) {

    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
  }


    loadDeals() {
    let param: any = {
      page: 0,
      limit: 10000,
      where: applyCenterScopeToWhere(
        { client_id: this.localStorage.getClientId() },
        this.localStorage
      ),
      order: {
        col: '"deal"."updatedAt"',
        dir: 'DESC'
      }
    };

    if (!this.isAdmin) {
      param.where.deal_owner = this.localStorage.getUserId();
    }

    this.dealService.getAllDeal(param).subscribe({
      next: (response: any) => {
        this.deals = Array.isArray(response?.rows) ? response.rows : [];
      },
      error: (error: any) => {
        console.error('Error loading deals:', error);
        this.toast.error('Failed to load deals');
      }
    });
  }

  handleShowCanban(event: any) {
    this.isToggled = event;
  }

  /** Strip quote email action query params without changing route (avoids CRM redirects). */
  private clearQuoteEmailQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
  }

  /** Plain-text message from accept/reject HTML returned by the API. */
  private messageFromQuoteActionHtml(html: string): string | null {
    if (typeof html !== 'string') return null;
    const m = html.match(/<main[^>]*>[\s\S]*?<p>([^<]*)<\/p>/i);
    return m && m[1] ? m[1].trim() : null;
  }

  ngAfterViewInit() {
    console.log("table column : ", this.displayedColumns);
    this.currencyCode = this.localStorage.getClientCurrency();

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const action = String(params['quoteRespond'] || '').toLowerCase();
      const quoteIdRaw = params['quote_id'] ?? params['quoteId'];
      const clientIdRaw = params['client_id'];
      const hasEmailLink =
        (action === 'accept' || action === 'reject') &&
        quoteIdRaw != null &&
        quoteIdRaw !== '' &&
        clientIdRaw != null &&
        clientIdRaw !== '';

      if (hasEmailLink) {
        const quoteId = Number(quoteIdRaw);
        const clientId = Number(clientIdRaw);
        if (!Number.isFinite(quoteId) || !Number.isFinite(clientId)) {
          this.toast.error('Invalid quote link.');
          this.clearQuoteEmailQueryParams();
          this.loadData();
          return;
        }
        const processing = this.toast.info(
          action === 'accept' ? 'Accepting quote…' : 'Rejecting quote…',
          '',
          { disableTimeOut: true, positionClass: 'toast-top-right' }
        );
        this.reloading = true;
        const req$ =
          action === 'accept'
            ? this.api.acceptQuoteFromLink(quoteId, clientId, {
                from_email: this.localStorage.getSendEmailFromAddress(),
                emailProviderType: this.localStorage.getEmailProviderTypeForPayload(),
              })
            : this.api.rejectQuoteFromLink(quoteId, clientId);
        req$.subscribe({
          next: (res: any) => {
            this.toast.clear(processing.toastId);
            const msg =
              typeof res === 'string'
                ? this.messageFromQuoteActionHtml(res) ||
                  (action === 'accept'
                    ? 'Quote accepted.'
                    : 'Quote rejected.')
                : res?.message ||
                  (action === 'accept' ? 'Quote accepted.' : 'Quote rejected.');
            this.toast.success(msg);
            const desc = `Quote ${action} via email link, id: ${quoteId}`;
            this.tracker.track(
              action === 'accept' ? 'Accept' : 'Reject',
              'Quote',
              desc
            );
            this.clearQuoteEmailQueryParams();
            this.reloading = false;
            this.loadData();
          },
          error: (err: any) => {
            this.toast.clear(processing.toastId);
            this.toast.error(err.error?.message || 'Request failed.');
            this.clearQuoteEmailQueryParams();
            this.reloading = false;
            this.loadData();
          }
        });
        return;
      }

      this.loadData();
    });
  }

  ngOnInit(): void {
    this.getAllUsers();
    this.loadDeals();
  }

  getAllUsers() {
    let param = {
      client_id: this.localStorage.getClientId(),
    };
    this.userApi.findAllUsers(param).subscribe({
      next: (res: any) => {
        this.users = res || [];
      },
      error: (e: any) => {
        console.error(e);
      },
    });
  }

  open(content: any) {
    this.formAction = 'Add';

    this.modalService.open(content, {
      size: 'xl', scrollable: true,
      centered: true
    })
  }

  loadData() {
     this.reloading = true;
    try {
      merge(
        this.dateQuery.valueChanges,
        this.searchQuery.valueChanges,
        this.sort.sortChange,
        this.paginator.page
      )
        .pipe(
          startWith({}),
          switchMap(() => {
            this.reloading = true;

            console.log("client_id", this.localStorage.getClientId())
            let param: any = {
              "page": this.paginator.pageIndex * this.paginator.pageSize,
              "limit": this.paginator.pageSize,
              where: {
                client_id: this.localStorage.getClientId(),
              },
              "order": {
                "col": "\"quote\".\"updatedAt\"",
                "dir": "DESC"
              }
            }

            if (this.searchQuery.value) {

              for (let [key, value] of Object.entries(this.searchQuery.value)) {

                if (this.defaultFilter.includes(key)) {

                  if (key == 'total_amount') {
                    param.where[key] = Number(value);
                  } else {
                    param.where[key] = value;
                  }
                }
              }
            }

            return this.api.getAllQuote(param).pipe(catchError(() => {
              this.reloading = false;
              this.initialLoad = false;
              return [];
            }));
          }),
          map((data: any) => {

            this.reloading = false;
            this.initialLoad = false;

            if (data) {
              this.totalRows = data.count || 0;
              return data.rows || [];
            } else {
              this.totalRows = 0;
              return [];
            }
          }),
        ).subscribe((data: any) => {
          this.dataSource.data = data;
        });
    } catch (error) {
      this.reloading = false;
      this.initialLoad = false;
    }
  }

  reloadData() {
    this.reloading = true;
    try {
      merge()
        .pipe(
          startWith({}),
          switchMap(() => {
            this.reloading = true;

            console.log("client_id", this.localStorage.getClientId())
            let param: any = {
              "page": this.paginator.pageIndex * this.paginator.pageSize,
              "limit": this.paginator.pageSize,
              where: {
                client_id: this.localStorage.getClientId(),
              },
              "order": {
                "col": "\"quote\".\"updatedAt\"",
                "dir": "DESC"
              }
            }

            if (this.searchQuery.value) {

              for (let [key, value] of Object.entries(this.searchQuery.value)) {

                if (this.defaultFilter.includes(key)) {

                  if (key == 'total_amount') {
                    param.where[key] = Number(value);
                  } else {
                    param.where[key] = value;
                  }
                }
              }
            }

            return this.api.getAllQuote(param).pipe(catchError(() => {
              this.reloading = false;
              this.initialLoad = false;
              return [];
            }));
          }),
          map((data: any) => {

            this.reloading = false;
            this.initialLoad = false;

            if (data) {
              this.totalRows = data.count || 0;
              return data.rows || [];
            } else {
              this.totalRows = 0;
              return [];
            }
          }),
        ).subscribe((data: any) => {
          this.dataSource.data = data;
        });
    } catch (error) {
      this.reloading = false;
      this.initialLoad = false;
    }
  }

  openEdit(content: any, data: any) {
    this.formAction = 'Update';
    this.editData = data;
    this.modalService.open(content, {
      size: 'lg', scrollable: true,
      centered: true
    })
  }

  openDeleteEvent(content: any, data: any) {
    this.deleteData = data;
    this.modalService.open(content, {
      size: 'md', scrollable: true,
      centered: true
    })
  }

  searchFilter(query: any) {
    console.log('Search Query:', query);
    this.searchQuery.setValue(query);
  }

  handleDeleteQuote() {
    const params = {
      "id": this.deleteData.id,
      "client_id": this.localStorage.getClientId(),
      "user_id": this.localStorage.getUserId()
    }
    this.btnLoading = true;
    this.api.deleteQuote(params).subscribe((res: any) => {
      console.log('Response:', res);
      this.toast.success('Quote Deleted Successfully');

      const desc = `Quote deleted with number: ${this.deleteData.quote_number} and id: ${this.deleteData.id}`;
      this.tracker.track("Delete", "Quote", desc);
      this.reloadData();
      this.btnLoading = false;
      this.modalService.dismissAll();
    })
  }

  openSendQuote(content: any, data: any) {
    this.editData = data;
    this.sendQuoteFromEmail = this.localStorage.getSendEmailFromAddress();
    this.sendQuoteEmailProvider = this.localStorage.getEmailProviderTypeForPayload();
    this.sendQuoteToEmail = this.resolveAccountEmail(data);
    this.modalService.open(content, {
      size: 'md',
      scrollable: true,
      centered: true
    });

    if (!this.sendQuoteToEmail && data?.account_id) {
      this.loadQuoteAccountEmail(data.account_id);
    }
  }

  private resolveAccountEmail(data: any): string {
    return String(
      data?.account?.email ||
      data?.account?.fields?.email ||
      data?.account_email ||
      data?.email ||
      ''
    ).trim();
  }

  private loadQuoteAccountEmail(accountId: any): void {
    const params = {
      page: 0,
      limit: 1,
      where: applyCenterScopeToWhere(
        {
          client_id: this.localStorage.getClientId(),
          account_id: accountId,
        },
        this.localStorage
      ),
      order: {
        col: '"account"."updatedAt"',
        dir: 'DESC',
      },
    };

    this.companyService.getAllCompany(params).subscribe({
      next: (response: any) => {
        const account = response?.rows?.[0];
        this.sendQuoteToEmail = this.resolveAccountEmail({ account });
        if (account && this.editData) {
          this.editData.account = { ...this.editData.account, ...account };
        }
      },
      error: (error: any) => {
        console.error('Failed to load quote account email:', error);
      },
    });
  }

  handleSendQuote() {
    if (!this.sendQuoteEmailProvider || !this.sendQuoteFromEmail) {
      this.toast.error('Set an email preference or connect an email account before sending');
      return;
    }
    if (!this.sendQuoteToEmail) {
      this.toast.error('The selected account does not have an email address');
      return;
    }

    this.btnLoading = true;
    const params = {
      "id": this.editData.id,
      "client_id": this.localStorage.getClientId(),
      "user_id": this.localStorage.getUserId(),
      "emailProviderType": this.sendQuoteEmailProvider,
      "companyName": this.editData.account?.account_name,
      "from_email": this.sendQuoteFromEmail
    }
    
    this.api.sendQuote(params).subscribe({
      next: (res: any) => {
        this.toast.success('Quote Sent Successfully');
        const desc = `Quote sent with number: ${this.editData.quote_number} and id: ${this.editData.id}`;
        this.tracker.track("Send", "Quote", desc);
        this.reloadData();
        this.btnLoading = false;
        this.modalService.dismissAll();
      },
      error: (error: any) => {
        this.toast.error(error.error?.message || 'Failed to send quote');
        this.btnLoading = false;
      }
    });
  }

  openExternalUrl(url: string | null | undefined): void {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  handleDownloadQuote(element: any) {
    const toastRef = this.toast.info('Downloading quote...', '', {
      disableTimeOut: true,
      positionClass: 'toast-top-right'
    });

    const params = {
      quote_id: element.id
    };

    this.api.generateQuote(params).subscribe({
      next: (res: any) => {
        this.toast.clear(toastRef.toastId);

        const fileUrl = res.fileUrl || res.file_url;
        const filename = `${res.quote_number || 'quote'}.pdf`;

        if (res.success && res.pdfBase64) {
          // Convert base64 to blob
          const byteCharacters = atob(res.pdfBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.toast.success('Quote downloaded successfully', '', {
            positionClass: 'toast-top-right'
          });

          const desc = `Quote downloaded with number: ${res.quote_number} and id: ${res.quote_id}`;
          this.tracker.track("Download", "Quote", desc);
        } else if (res.success && fileUrl) {
          // API uploads to S3 and returns a URL (no pdfBase64 in that case)
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.toast.success('Quote downloaded successfully', '', {
            positionClass: 'toast-top-right'
          });

          const desc = `Quote downloaded with number: ${res.quote_number} and id: ${res.quote_id}`;
          this.tracker.track("Download", "Quote", desc);
        } else {
          this.toast.error('Failed to generate PDF', '', {
            positionClass: 'toast-top-right'
          });
        }
      },
      error: (err: any) => {
        this.toast.clear(toastRef.toastId);
        this.toast.error(err.error?.message || 'Failed to download quote', '', {
          positionClass: 'toast-top-right'
        });
      }
    });
  }

  updateHoverState(id: string): void {
    this.current_id_hover = id;
  }

}